/**
 * Route names supported by the active MeshOS website.
 */
export type RouteName =
  | 'home'
  | 'login'
  | 'account'
  | 'download'
  | 'signup'
  | 'request-invite'
  | 'verifyemail'
  | 'terms'
  | 'not-found';

/**
 * Result of matching a path against active routes.
 */
export interface RouteMatch {
  /** Matched route name. */
  name: RouteName;
  /** Dynamic route params. */
  params: Record<string, string>;
  /** Whether this route requires auth. */
  protected: boolean;
  /** Browser document title for the route. */
  title: string;
}

const cleanPath = (path: string): string => {
  const withoutHash = path.split('#')[0] ?? '';
  const withoutSearch = withoutHash.split('?')[0] ?? '';
  if (withoutSearch.length > 1 && withoutSearch.endsWith('/')) {
    return withoutSearch.slice(0, -1);
  }
  return withoutSearch || '/';
};

/**
 * Matches a URL path against only the active production routes.
 */
export function matchRoute(path: string): RouteMatch {
  const pathname = cleanPath(path);

  if (pathname === '/') {
    return {
      name: 'home',
      params: {},
      protected: false,
      title: 'MeshOS: Next-generation working environment',
    };
  }

  if (pathname === '/login') {
    return {
      name: 'login',
      params: {},
      protected: false,
      title: 'MeshOS Login',
    };
  }

  const loginMatch = /^\/login\/([^/]+)$/.exec(pathname);
  if (loginMatch) {
    return {
      name: 'login',
      params: { redirect: loginMatch[1] ?? '' },
      protected: false,
      title: 'MeshOS Login',
    };
  }

  if (pathname === '/account') {
    return {
      name: 'account',
      params: {},
      protected: true,
      title: 'MeshOS: Account Management',
    };
  }

  if (pathname === '/download') {
    return {
      name: 'download',
      params: {},
      protected: false,
      title: 'MeshOS: Download App',
    };
  }

  const downloadMatch = /^\/download\/([^/]+)$/.exec(pathname);
  if (downloadMatch) {
    return {
      name: 'download',
      params: { newUser: downloadMatch[1] ?? '' },
      protected: false,
      title: 'MeshOS: Download App',
    };
  }

  if (pathname === '/signup') {
    return {
      name: 'signup',
      params: {},
      protected: false,
      title: 'MeshOS: Sign Up',
    };
  }

  const signupMatch = /^\/signup\/([^/]+)$/.exec(pathname);
  if (signupMatch) {
    return {
      name: 'signup',
      params: { ref: signupMatch[1] ?? '' },
      protected: false,
      title: 'MeshOS: Sign Up',
    };
  }

  if (pathname === '/request-invite') {
    return {
      name: 'request-invite',
      params: {},
      protected: false,
      title: 'MeshOS: Request an Invitation',
    };
  }

  if (pathname === '/verifyemail') {
    return {
      name: 'verifyemail',
      params: {},
      protected: false,
      title: 'MeshOS: Verify Email',
    };
  }

  if (pathname === '/terms') {
    return {
      name: 'terms',
      params: {},
      protected: false,
      title: 'MeshOS: Terms of Service',
    };
  }

  return {
    name: 'not-found',
    params: {},
    protected: false,
    title: 'MeshOS: Page Not Found',
  };
}

/**
 * Sets the browser document title for an active route path.
 */
export function setTitleForPath(path: string): void {
  document.title = matchRoute(path).title;
}
